// Last-resort sentinel only: the real dev/feature-instance value is derived from
// `.appversion-prefix` by `versionEnv()` and arrives via `NEXT_PUBLIC_APP_VERSION`, so this
// literal is hit solely when that inlined value is missing (a misconfiguration).
const DEV_APP_VERSION = '0.0.0-dev';
const UNKNOWN_MFE_NAME = 'unknown';
const DEV_MFE_VERSION = '0.0.0';
const UNKNOWN_COMMIT = 'unknown';
const UNKNOWN_BUILD_NUMBER = 'unknown';

export type MfeHealth = {
    status: 'healthy';
    appVersion: string;
    mfeName: string;
    mfeVersion: string;
    /** CI job/build number (`NEXT_PUBLIC_BUILD_NUMBER`, written by `workflow/bin/publish-build.sh`). */
    buildNumber: string;
    buildTimestamp: string;
    commitHash: string;
    nodeVersion: string;
};

/**
 * Builds a single MFE's health payload from its own build-time env vars only.
 *
 * No external calls — every value is read from `process.env` (the `NEXT_PUBLIC_*`
 * keys are inlined at build time by `versionEnv()`; `GIT_COMMIT` is a server-side
 * Docker `ENV`). Shared across every per-MFE `/api/tn/{mfe}/health` route and the
 * appshell aggregator (`resolveGlobalVersion()`, which reads its own data directly
 * instead of calling itself).
 *
 * Empty-string env vars are treated as unset (`||`, not `??`) to match the repo
 * convention in `getAppVersion` / `versionEnv` — an empty build arg must fall
 * through to the dev fallback rather than surface a blank version.
 */
export function getMfeHealth(): MfeHealth {
    return {
        status: 'healthy',
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION || DEV_APP_VERSION,
        mfeName: process.env.NEXT_PUBLIC_MFE_NAME || UNKNOWN_MFE_NAME,
        mfeVersion: process.env.NEXT_PUBLIC_MFE_VERSION || DEV_MFE_VERSION,
        buildNumber: process.env.NEXT_PUBLIC_BUILD_NUMBER || UNKNOWN_BUILD_NUMBER,
        buildTimestamp: process.env.NEXT_PUBLIC_BUILD_TIMESTAMP || '',
        commitHash: process.env.GIT_COMMIT || UNKNOWN_COMMIT,
        nodeVersion: process.version,
    };
}
